/**
 * The Action Plan (SYR-68, Phases 3–4).
 *
 * The recommendation plays the marketer committed (from the Recommendations
 * tab) become trackable tasks: a progress header + per-play groups + per-task
 * done checkboxes, with the same Impact / Effort / moves chips. Committed plays
 * and done state are persisted server-side per (org, brand). Each play renders
 * from its commit-time snapshot, so it survives Briefing AI regeneration; the
 * `moves` chip resolves the element's current score from the live BE data.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal } from 'antd';
import confetti from 'canvas-confetti';
import { CheckOutlined, CheckSquareOutlined, CloseOutlined, TrophyOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetMarketerActionPlanQuery,
  useGetMarketerActionPlanHistoryQuery,
  useSetMarketerActionPlanDoneMutation,
  useRemoveMarketerActionPlanPlayMutation,
  useArchiveMarketerActionPlanMutation,
  useSetMarketerActionPlanAssigneeMutation,
  useGetUsersQuery,
} from '../../../services/nestjs-base-api';
import { useBriefingData } from '../useBriefingData';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { MarketerLoading } from '../atoms/MarketerLoading';
import { Card } from '../atoms';
import { THEME } from '../config/theme';
import { MarketerPageHeader } from './MarketerPageHeader';
import { ObjectiveChips } from '../components/ObjectiveChips';
import { ObjectiveAskExpert } from '../components/ObjectiveAskExpert';
import { ActionPlanHistory } from '../components/ActionPlanHistory';
import { AssigneePicker, userDisplayName, type OrgUser } from '../components/AssigneePicker';
import type { Recommendation } from '../components/RecommendationCard';
import type { ScoreNode } from '../types';

const FIX = '#DC2626';
const WIN = '#16A34A';

export const ActionPlanPage: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const { activeBrand } = useMarketerScope();
  const brandId = activeBrand?.id ?? 0;
  const marketerRoot = useMarketerRoot();
  const { data, isLoading } = useBriefingData();

  // Committed plays + done state (server-persisted per org+brand).
  const canQuery = !!jwtToken && !!organisationId && !!brandId;
  const {
    data: committedPlays,
    isLoading: planLoading,
    refetch,
  } = useGetMarketerActionPlanQuery(
    { jwtToken, organisationId, brandId },
    { skip: !canQuery },
  );
  const [setDone] = useSetMarketerActionPlanDoneMutation();
  const [removePlay] = useRemoveMarketerActionPlanPlayMutation();
  const [archivePlan] = useArchiveMarketerActionPlanMutation();
  const [setAssignee] = useSetMarketerActionPlanAssigneeMutation();

  // Org users — power the assignee picker + resolve creator/assignee names.
  // NOTE: /users/api-users returns a BARE array; the RTK hook's `{ data }`
  // return type is misleading (OrganisationUsers casts it the same way).
  const { data: usersData, isLoading: usersLoading } = useGetUsersQuery(
    { jwtToken },
    { skip: !jwtToken },
  );
  const orgUsers = useMemo<OrgUser[]>(
    () => (usersData as unknown as OrgUser[] | undefined) ?? [],
    [usersData],
  );
  const userById = useMemo(() => {
    const m = new Map<number, OrgUser>();
    orgUsers.forEach((u) => m.set(u.id, u));
    return m;
  }, [orgUsers]);

  // Active plan vs. completed-plans history / streak.
  const [view, setView] = useState<'active' | 'history'>('active');
  const { data: historyPlays, isLoading: historyLoading } =
    useGetMarketerActionPlanHistoryQuery(
      { jwtToken, organisationId, brandId },
      { skip: !canQuery || view !== 'history' },
    );

  // Resolve `moves` element → ScoreNode (canonical or display).
  const byName = useMemo(() => {
    const m = new Map<string, ScoreNode>();
    (data?.signals ?? []).forEach((s) => {
      m.set(s.canonical, s);
      m.set(s.display, s);
    });
    return m;
  }, [data?.signals]);
  const resolve = useCallback((name: string) => byName.get(name), [byName]);

  // FIX-numbered plays, rendered from each committed snapshot (created order).
  const plays = useMemo(() => {
    let f = 0;
    return (committedPlays ?? []).map((p) => {
      const card = (p.snapshotJson ?? {}) as Recommendation;
      return {
        playId: p.playId,
        card,
        objectives: card.objectives ?? [],
        done: new Set(p.doneObjectives ?? []),
        fixNo: card.type === 'fix' ? ++f : null,
        createdByUserId: p.createdByUserId ?? null,
        assignedToUserId: p.assignedToUserId ?? null,
        createdByName: p.createdByName ?? null,
        assignedToName: p.assignedToName ?? null,
        createdAt: p.createdAt ?? null,
      };
    });
  }, [committedPlays]);

  const toggleObjective = useCallback(
    async (playId: string, current: Set<number>, index: number) => {
      if (!canQuery) return;
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      try {
        await setDone({
          jwtToken,
          organisationId,
          brandId,
          playId,
          done: Array.from(next).sort((a, b) => a - b),
        }).unwrap();
        refetch();
      } catch {
        /* surfaced by RTK; leave UI state to the refetch */
      }
    },
    [canQuery, setDone, refetch, jwtToken, organisationId, brandId],
  );

  // Remove a committed play — the only way to drop a play whose live
  // recommendation has regenerated away (it no longer has a card to un-tick).
  const remove = useCallback(
    async (playId: string) => {
      if (!canQuery) return;
      try {
        await removePlay({ jwtToken, organisationId, brandId, playId }).unwrap();
        refetch();
      } catch {
        /* surfaced by RTK; leave UI state to the refetch */
      }
    },
    [canQuery, removePlay, refetch, jwtToken, organisationId, brandId],
  );

  // Reassign a play to another org user (or null to unassign).
  const assign = useCallback(
    async (playId: string, userId: number | null) => {
      if (!canQuery) return;
      try {
        await setAssignee({ jwtToken, organisationId, brandId, playId, userId }).unwrap();
        refetch();
      } catch {
        /* surfaced by RTK; leave UI state to the refetch */
      }
    },
    [canQuery, setAssignee, refetch, jwtToken, organisationId, brandId],
  );

  // Progress — computed before the early returns so the confetti effect can
  // watch it (hooks must run unconditionally).
  const { totalTasks, doneTasks, pct, complete } = useMemo(() => {
    let total = 0;
    let done = 0;
    plays.forEach((p) => {
      p.objectives.forEach((_, i) => {
        total += 1;
        if (p.done.has(i)) done += 1;
      });
    });
    return {
      totalTasks: total,
      doneTasks: done,
      pct: total ? Math.round((done / total) * 100) : 0,
      complete: total > 0 && done === total,
    };
  }, [plays]);

  // Fire confetti the moment the plan tips to 100% — only on that transition,
  // not on every visit to an already-complete plan. A null baseline on first
  // run (mount / initial load) suppresses the fire for a plan that loads done.
  const prevComplete = useRef<boolean | null>(null);
  useEffect(() => {
    const wasComplete = prevComplete.current;
    prevComplete.current = complete;
    if (wasComplete === null) return; // baseline only
    if (complete && !wasComplete) {
      confetti({
        particleCount: 140,
        spread: 75,
        startVelocity: 45,
        origin: { y: 0.6 },
        scalar: 1.05,
      });
    }
  }, [complete]);

  // "Clear" the completed plan — archives every play (kept for history) as the
  // completion reward, then refetches to the empty state.
  const clearPlan = useCallback(() => {
    if (!canQuery) return;
    Modal.confirm({
      title: intl.formatMessage({
        id: 'marketerView.actionPlan.clearConfirmTitle',
        defaultMessage: 'Clear your completed plan?',
      }),
      content: intl.formatMessage({
        id: 'marketerView.actionPlan.clearConfirmBody',
        defaultMessage:
          "Nice work, every move is done. We'll file this plan away and give you a clean slate. You can build a new plan from your recommendations any time.",
      }),
      okText: intl.formatMessage({
        id: 'marketerView.actionPlan.clearConfirmOk',
        defaultMessage: 'Clear it',
      }),
      cancelText: intl.formatMessage({
        id: 'marketerView.actionPlan.clearConfirmCancel',
        defaultMessage: 'Keep it',
      }),
      onOk: async () => {
        try {
          await archivePlan({ jwtToken, organisationId, brandId }).unwrap();
          refetch();
        } catch {
          /* surfaced by RTK */
        }
      },
    });
  }, [canQuery, archivePlan, refetch, intl, jwtToken, organisationId, brandId]);

  const viewToggle = (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        gap: 3,
        background: THEME.bgTinted,
        border: `1px solid ${THEME.borderSubtle}`,
        borderRadius: 11,
      }}
    >
      {(['active', 'history'] as const).map((v) => {
        const selected = view === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            aria-pressed={selected}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '7px 14px',
              fontSize: 13.5,
              fontWeight: 600,
              cursor: 'pointer',
              background: selected ? '#fff' : 'transparent',
              color: selected ? THEME.textBase : THEME.textMuted,
              boxShadow: selected ? THEME.cardShadow : 'none',
            }}
          >
            {v === 'active' ? (
              <FormattedMessage id="marketerView.actionPlan.viewActive" defaultMessage="Active plan" />
            ) : (
              <FormattedMessage id="marketerView.actionPlan.viewHistory" defaultMessage="History" />
            )}
          </button>
        );
      })}
    </div>
  );

  const header = (
    <MarketerPageHeader
      title={<FormattedMessage id="marketerView.nav.actionPlan" defaultMessage="The Action Plan" />}
      subtitle={
        view === 'history' ? (
          <FormattedMessage
            id="marketerView.actionPlan.subHistory"
            defaultMessage="The plans you've completed and cleared. Keep the streak going."
          />
        ) : (
          <FormattedMessage
            id="marketerView.actionPlan.sub"
            defaultMessage="The moves you've committed to. Work through them top to bottom."
          />
        )
      }
      actions={viewToggle}
    />
  );

  // History / streak view — completed plans read back from the archive.
  if (view === 'history') {
    return (
      <div>
        {header}
        <ActionPlanHistory plays={historyPlays} isLoading={historyLoading} />
      </div>
    );
  }

  // Loading: BE data (for the moves chips) or the committed plan still resolving.
  if ((isLoading && !data) || (canQuery && planLoading)) {
    return (
      <div>
        {header}
        <MarketerLoading />
      </div>
    );
  }

  if (plays.length === 0) {
    return (
      <div>
        {header}
        <Card>
          <div style={{ textAlign: 'center', padding: '56px 24px' }}>
            <CheckSquareOutlined style={{ fontSize: 40, color: '#CBD5E1' }} />
            <h2
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: THEME.textBase,
                letterSpacing: '-0.02em',
                margin: '16px 0 8px',
              }}
            >
              <FormattedMessage
                id="marketerView.actionPlan.emptyTitle"
                defaultMessage="No moves committed yet"
              />
            </h2>
            <p style={{ fontSize: 15, color: THEME.textSecondary, margin: '0 auto 18px', maxWidth: 460 }}>
              <FormattedMessage
                id="marketerView.actionPlan.emptyBody"
                defaultMessage="Head to the Recommendations tab and add the fixes and quick wins you'll take on. They'll appear here as trackable tasks."
              />
            </p>
            <button
              type="button"
              onClick={() =>
                activeBrand && navigate(`${marketerRoot}/${activeBrand.hash}?tab=recommendations`)
              }
              style={{
                border: 'none',
                background: THEME.primary,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                padding: '11px 18px',
                borderRadius: 12,
                cursor: 'pointer',
              }}
            >
              <FormattedMessage
                id="marketerView.actionPlan.goRecos"
                defaultMessage="Go to recommendations →"
              />
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {header}

      {/* Completion reward — appears once every move is done. */}
      {complete && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
            border: '1px solid #6EE7B7',
            borderRadius: 14,
            padding: '16px 20px',
            marginBottom: 18,
          }}
        >
          <TrophyOutlined style={{ fontSize: 28, color: '#059669', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#065F46' }}>
              <FormattedMessage
                id="marketerView.actionPlan.completeTitle"
                defaultMessage="Plan complete, every move is done 🎉"
              />
            </div>
            <div style={{ fontSize: 13, color: '#047857', marginTop: 2 }}>
              <FormattedMessage
                id="marketerView.actionPlan.completeBody"
                defaultMessage="Clear this plan to file it away and start fresh from your recommendations."
              />
            </div>
          </div>
          <button
            type="button"
            onClick={clearPlan}
            style={{
              flexShrink: 0,
              border: 'none',
              background: '#059669',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              padding: '10px 18px',
              borderRadius: 11,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <FormattedMessage
              id="marketerView.actionPlan.clear"
              defaultMessage="Clear the plan"
            />
          </button>
        </div>
      )}

      {/* Progress */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          background: THEME.bgCard,
          border: `1px solid ${THEME.border}`,
          borderRadius: 14,
          padding: '16px 20px',
          marginBottom: 18,
          boxShadow: THEME.cardShadow,
        }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: THEME.textBase }}>
            <FormattedMessage
              id="marketerView.actionPlan.progress"
              defaultMessage="{done} of {total} tasks done · {plays} {plays, plural, one {play} other {plays}}"
              values={{ done: doneTasks, total: totalTasks, plays: plays.length }}
            />
          </div>
          <div style={{ fontSize: 12.5, color: THEME.textMuted, marginTop: 2 }}>
            <FormattedMessage
              id="marketerView.actionPlan.progressSub"
              defaultMessage="Ordered by leverage. Start at the top."
            />
          </div>
        </div>
        <div style={{ flex: 1, height: 8, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: WIN, borderRadius: 999 }} />
        </div>
        <div style={{ flex: '0 0 auto', fontSize: 18, fontWeight: 800, color: THEME.textBase }}>
          {pct}%
        </div>
      </div>

      {/* Plays */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {plays.map(({ card, playId, objectives, done, fixNo, createdByUserId, assignedToUserId, createdByName, assignedToName, createdAt }) => {
          const accent = card.type === 'win' ? WIN : FIX;
          const pDone = objectives.filter((_, i) => done.has(i)).length;
          // Prefer the server-resolved name (covers cross-org / platform-admin
          // creators), falling back to the org-users list.
          const creatorName =
            createdByName ??
            (createdByUserId != null ? userDisplayName(userById.get(createdByUserId)) : '') ??
            '';
          const addedDate =
            createdAt && !Number.isNaN(new Date(createdAt).getTime())
              ? intl.formatDate(new Date(createdAt), { day: 'numeric', month: 'short', year: 'numeric' })
              : '';
          return (
            <Card key={playId}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                <span style={{ width: 4, height: 22, borderRadius: 2, background: accent, flexShrink: 0 }} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: '#fff',
                    padding: '3px 10px',
                    borderRadius: 7,
                    background: accent,
                  }}
                >
                  {card.type === 'win' ? (
                    <FormattedMessage id="marketerView.recos.quickWin" defaultMessage="QUICK WIN" />
                  ) : (
                    <FormattedMessage
                      id="marketerView.recos.fixN"
                      defaultMessage="FIX {n}"
                      values={{ n: fixNo ?? '' }}
                    />
                  )}
                </span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>{card.title}</span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: THEME.textMuted, whiteSpace: 'nowrap' }}>
                    <FormattedMessage
                      id="marketerView.actionPlan.assignedTo"
                      defaultMessage="Assigned to"
                    />
                  </span>
                  <AssigneePicker
                    users={orgUsers}
                    value={assignedToUserId}
                    valueLabel={assignedToName}
                    onChange={(uid) => assign(playId, uid)}
                    loading={usersLoading}
                  />
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: THEME.textMuted }}>
                  {pDone}/{objectives.length}
                </span>
                <button
                  type="button"
                  onClick={() => remove(playId)}
                  title={intl.formatMessage({
                    id: 'marketerView.actionPlan.remove',
                    defaultMessage: 'Remove from Action plan',
                  })}
                  aria-label={intl.formatMessage({
                    id: 'marketerView.actionPlan.remove',
                    defaultMessage: 'Remove from Action plan',
                  })}
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    border: `1px solid ${THEME.border}`,
                    background: '#fff',
                    color: THEME.textMuted,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                  }}
                >
                  <CloseOutlined />
                </button>
              </div>
              {(creatorName || addedDate) && (
                <div style={{ margin: '-6px 0 14px', paddingLeft: 16, fontSize: 12, color: THEME.textMuted }}>
                  {creatorName ? (
                    <FormattedMessage
                      id="marketerView.actionPlan.addedBy"
                      defaultMessage="Added by {name}"
                      values={{ name: creatorName }}
                    />
                  ) : (
                    <FormattedMessage id="marketerView.actionPlan.added" defaultMessage="Added" />
                  )}
                  {addedDate && ` · ${addedDate}`}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {objectives.map((o, i) => {
                  const isDone = done.has(i);
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <button
                        type="button"
                        onClick={() => toggleObjective(playId, done, i)}
                        aria-pressed={isDone}
                        style={{
                          flexShrink: 0,
                          marginTop: 1,
                          width: 22,
                          height: 22,
                          borderRadius: 6,
                          border: `1px solid ${isDone ? '#16A34A' : '#CBD5E1'}`,
                          background: isDone ? '#16A34A' : '#fff',
                          color: '#fff',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                        }}
                      >
                        {isDone && <CheckOutlined />}
                      </button>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#0F172A',
                            lineHeight: 1.4,
                            textDecoration: isDone ? 'line-through' : 'none',
                            opacity: isDone ? 0.6 : 1,
                          }}
                        >
                          {o.task}
                        </div>
                        {o.detail && (
                          <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5, marginTop: 3 }}>
                            {o.detail}
                          </div>
                        )}
                        <ObjectiveChips objective={o} resolve={resolve} />
                        {!isDone && (
                          <ObjectiveAskExpert cardTitle={card.title} objective={o} resolve={resolve} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
